import * as React from 'react';
import styled from 'styled-components';

import Logo from './Logo';
import MainMenu from './desktop/MainMenu';
import {PageWidth} from '../Layouts';
import {COLORS, BREAKPOINTS} from '../styles';
import RightSideNav from './desktop/RightSideNav';
import MobileMenu from './mobile/MobileMenu';
import {SystemIcons} from '../icons';
import UserMenu from './desktop/UserMenu';
import {fadeIn, fadeOut, Menu as MobileMenuWrapper} from './mobile/CommonStyles';
import {Z_INDEXES} from '../styles';
import {
  DesktopNavigationMenuProps, MenuNavigationItemTypeItem, MenuProfileButton,
  MobileNavigationMenuProps,
  ProfileMenu
} from './types';
import {IconButton} from '../Button';
import DesktopActions, {DesktopActionsRef} from './desktop/DesktopActions';
import {usePreviousImmediate} from 'rooks';
import {useClickOutsideRef} from '../common';
import { hasWindow } from '../utils/utils';

const HeaderWrapper = styled.header`
  background: white;
  box-sizing: border-box;
  
  &.border:after{
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background: ${COLORS.neutral_200};
    z-index: 5;
  }

  z-index: ${Z_INDEXES.sticky_menu};
  position: relative;

  height: 48px;

  ${BREAKPOINTS.MEDIUM} {
    height: 64px;
  }

  ${BREAKPOINTS.LARGE} {
    height: 80px;
  }

  @media all and (-ms-high-contrast: none) {
    height: auto;
  }

  @media print {
    border-bottom: none;
  }
`;

const RowLayout = styled.div`
  display: flex;
  align-items: center;
`;


const HamburgerButton = styled.div`
  ${BREAKPOINTS.MEDIUM} {
    display: none;
  }
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;

  margin: 0 0 0 auto;

  &.reverse {
    ${HamburgerButton} {
      order: -1;
    }

    flex-direction: row-reverse;
  }
`;

const MobileMenuBackdrop = styled.div`
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  z-index: ${Z_INDEXES.backdrop};
  visibility: hidden;

  &.open {
    visibility: visible;
    animation: ${fadeIn} 0.3s ease-in-out;
  }

  &.closed {
    visibility: hidden;
    animation: ${fadeOut} 0.3s ease-in-out;
  }

  ${BREAKPOINTS.LARGE} {
    visibility: hidden !important;
  }
`;

const MobileWrapper = styled.div`
  ${BREAKPOINTS.MEDIUM} {
    display: none !important;
  }
`;


const UserMenuWrapper = styled.div<{ offsetRight?: number }>`
  background-color: ${COLORS.white};
  display: flex;
  position: absolute;

  ${BREAKPOINTS.LARGE} {
    top: 80px;
    right: ${(props) => (props.offsetRight && hasWindow() ? `${window.innerWidth - props.offsetRight + 296}px` : '10%')};
  }
`;



type GlobalNavigationBarProps = {
  isAuthenticated?: boolean;
  useMaxWidth?: boolean;
  maxWidth?: number;

  name?: string;
  logo?: { onClick?: () => void; to?: string; showBetaTag?: boolean; };

  profile?: ProfileMenu;

  mobile?: MobileNavigationMenuProps;
  desktop?: DesktopNavigationMenuProps;

  testId?: string;

  profileComponent?: React.ReactNode
  border?: boolean;
};

/**
 * @param props
 * @constructor
 * @description This component is a global navigation header bar
 */
const GlobalNavigationBar = ({
                               name,
                               isAuthenticated,
                               logo,
                               profile,
                               mobile,
                               desktop,
                               testId,
                               useMaxWidth = true,
                               maxWidth,
                               profileComponent,
                               border = true
                             }: GlobalNavigationBarProps): React.ReactElement<GlobalNavigationBarProps> => {
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);
  const wasMobileMenuOpen = usePreviousImmediate(showMobileMenu);

  const [showUserMenu, setShowUserMenu] = React.useState(false);

  const actionsRef = React.useRef<DesktopActionsRef>(null);
  const profileButtonRef = React.useRef<HTMLButtonElement | null>() as React.MutableRefObject<HTMLButtonElement | null | undefined>;
  profileButtonRef.current = actionsRef?.current?.buttons?.find(a => a.type === 'profile')?.ref?.current;

  const mobileMenuButtonRef = React.useRef<HTMLButtonElement>(null);

  const rightSideRef = React.useRef(null);
  const [avatarRight, setAvatarRight] = React.useState<number>(0);


  const userMenuRef = useClickOutsideRef(() => setShowUserMenu(false), [profileButtonRef]);
  const mobileMenuRef = useClickOutsideRef(() => setShowMobileMenu(false), [mobileMenuButtonRef]);

  React.useEffect(() => {
    if (!profileButtonRef.current) return;

    const handleResize = () => {
      const profileRightOffset = profileButtonRef.current?.getBoundingClientRect()?.right;
      setAvatarRight(profileRightOffset || 0);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [profileButtonRef.current]);


  return (
    <>
      <HeaderWrapper data-testid={testId} className={border ? 'border' : ''}>
        <PageWidth useMaxWidth={useMaxWidth} maxWidth={maxWidth} unsetMargin={true}>
          <RowLayout>
            <Logo name={name || ''} showBetaTag={logo?.showBetaTag} onClick={logo?.onClick} to={logo?.to}/>
            {
              isAuthenticated &&
              <MainMenu items={desktop?.items.filter(a => !a.pinned) || []} rightSideRef={rightSideRef}/>
            }
            <RightSide className={`${desktop?.reverseRightSideOrder ? 'reverse' : ''}`}
                       ref={rightSideRef}>
              <RightSideNav items={desktop?.items?.filter(a => a.pinned && a.type == 'item').map(x => x as MenuNavigationItemTypeItem)} action={desktop?.action}/>
              <MobileWrapper>
                {!!profileComponent && profileComponent }
              </MobileWrapper>

              <DesktopActions ref={actionsRef}
                              buttons={desktop?.buttons}
                              onShowUserMenu={() => setShowUserMenu(!showUserMenu)}
                              user={profile?.user}
                              profileComponent= {profileComponent}
                              />
              <HamburgerButton hidden={!isAuthenticated}>
                <IconButton action={() => setShowMobileMenu(!showMobileMenu)}
                            ref={mobileMenuButtonRef}
                            shape={'circular'}
                            variant={'secondary'}>
                  <SystemIcons.Menu size="24px"/>
                </IconButton>
              </HamburgerButton>
            </RightSide>
          </RowLayout>
        </PageWidth>
      </HeaderWrapper>
      <MobileMenuBackdrop className={showMobileMenu ? 'open' : !showUserMenu && wasMobileMenuOpen ? 'closed' : ''}>
        <MobileMenuWrapper ref={mobileMenuRef}
                           role="menu"
                           aria-labelledby="UserMenuButton">
          <MobileMenu {...mobile}
                      show={showMobileMenu}
                      onClose={() => setShowMobileMenu(false)}
                      profile={!profileComponent ? profile : undefined}/>
        </MobileMenuWrapper>
      </MobileMenuBackdrop>
      {
        desktop?.buttons?.some(a => a.type === 'profile') && !!profile &&
        <UserMenuWrapper ref={userMenuRef}
                         offsetRight={avatarRight}
                         data-testid="testUserMenuWrapper">
          <UserMenu {...profile}
                    show={showUserMenu}
                    onHideUserMenu={() => setShowUserMenu(false)}/>
        </UserMenuWrapper>
      }
    </>
  );
};

export default GlobalNavigationBar;
